/** @type {{ GETTING_STARTED: 'getting-started', OPERATIONS: 'operations', LIMITATIONS: 'limitations' }} */
export const GUIDE_TABS = {
  GETTING_STARTED: 'getting-started',
  OPERATIONS: 'operations',
  LIMITATIONS: 'limitations',
};

/** @type {{ GETTING_STARTED: 'Getting Started', OPERATIONS: 'Operations', LIMITATIONS: 'Limitations' }} */
export const TAB_LABELS = {
  GETTING_STARTED: 'Getting Started',
  OPERATIONS: 'Operations',
  LIMITATIONS: 'Limitations',
};

/** @type {{ CREATE: 'create', UPDATE: 'update', DELETE: 'delete' }} */
export const OPERATION_TYPES = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
};

/** @type {{ WARNING: 'warning', ERROR: 'error', INFO: 'info' }} */
export const ICON_TYPES = {
  WARNING: 'warning',
  ERROR: 'error',
  INFO: 'info',
};

export const GUIDE_CONTENT = {
  GETTING_STARTED: {
    WHAT_CAN_IT_DO: {
      TITLE: 'What can this do?',
      PARA_1: "Think of this as a smart assistant for your product variants. Instead of clicking through forms, just tell it what you want in plain English — and it handles the rest.",
      PARA_2_SEGMENTS: [
        { text: 'You can ', bold: false },
        { text: 'add', bold: true },
        { text: ' new variants, ', bold: false },
        { text: 'change', bold: true },
        { text: ' details on existing ones, or ', bold: false },
        { text: 'remove', bold: true },
        { text: ' variants you no longer need — all by typing a simple instruction.', bold: false },
      ],
    },
    HOW_TO_USE: {
      TITLE: 'How do I use it?',
      INTRO: "Just type what you want, like you're texting a colleague:",
      EXAMPLES: [
        '"Create 3 variants with SKUs A100, A101, A102 and weight 5kg"',
        '"Update weight to 3kg for all variants"',
        '"Delete variants with SKU HOSDOSD5 and HOSDOSD8"',
      ],
      OUTRO: "You don't need to know exact field names — just describe what you mean and the AI figures it out.",
    },
    VOCABULARY: {
      TITLE: 'What words can I use?',
      TABLE_HEADERS: ['If you say...', 'It understands...'],
      TABLE_ROWS: [
        ['"SKU", "sku number"', "The variant's SKU"],
        ['"weight 5 kg" or "weight 10 lbs"', 'The weight field'],
        ['"item type"', 'Item Type field'],
        ['"material number"', 'Material Number field'],
        ['"vendor part number"', 'Vendor Part Number field'],
        ['"enable purchase", "purchase flag"', 'Purchase toggle (on/off)'],
        ['"enable rent", "rent flag"', 'Rent toggle (on/off)'],
        ['"weighting in attribute", "attribute = X"', 'Attribute values'],
      ],
    },
    TIPS: {
      TITLE: 'A few tips before you start',
      ITEMS: [
        { bold: 'Stick to one thing at a time', body: " — for example, don't mix adding and deleting in the same message. Keep it focused." },
        { bold: 'Use the exact SKU', body: " — if you're targeting a specific variant, copy-paste the SKU to avoid typos." },
        { bold: 'Always mention the unit for weight', body: ' — say "5 kg" or "10 lbs", not just "5". It avoids confusion.' },
        { bold: 'Bulk creation made easy', body: " — need 50 variants? Just say \"SKU-100 to SKU-150\" and it'll generate them all." },
      ],
    },
  },

  OPERATIONS: {
    CREATE: {
      BADGE: 'CREATE',
      TITLE: 'Adding new variants',
      PARA_1: 'Want to add variants to this product? Just tell it how many and what SKUs to use. You can even fill in all the details in the same message.',
      EXAMPLES: [
        '"Create 2 variants having SKUs are "T2310D2" and "PDF23J""',
        '"Create 5 variants, its 1st SKU is "232DSS0D1" and other SKUs are in sequential order of 1st SKU. Also add details mentioned below for all variants:\n1- Item type is "ITHDSDS"\n2- Material number is "MNHKDS132"\n3- Enable Purchase and Rent flag\n4- Provide "Weighting" in attribute="999" for all variants\n5- Weight is 2 kg"',
      ],
      PARA_2: 'You can set as many fields as you like — weight, item type, material number, attributes, purchase/rent flags — all in one go.',
    },
    UPDATE: {
      BADGE: 'UPDATE',
      TITLE: 'Changing existing variants',
      PARA_1: 'Need to fix or update something on existing variants? You can change one field or several at once — across all variants or just specific SKUs.',
      EXAMPLES: [
        '"Update Weight=2 and Item Type="ITTest2" for all variants"',
        '"Set Weight=2.4, Item Type="ITTest2" and Material number="MNHKDS921" for variants having SKUs "HOSDOSD5", "HOSDOSD8" and "HOSDOSD1""',
      ],
      PARA_2: 'To update everyone, say "for all variants". To target specific ones, just list their SKUs.',
    },
    DELETE: {
      BADGE: 'DELETE',
      TITLE: 'Removing variants',
      PARA_1: 'You can remove variants by their SKU, by a range, or by a condition like weight. Just describe which ones you want gone.',
      EXAMPLES: [
        '"Delete variants having SKU "HOSDOSD5", "HOSDOSD8" and "HOSDOSD10""',
        '"Delete variants having weight is 2"',
        '"Delete SKUs between 10001 to 10005"',
      ],
      PARA_2: 'Heads up — once deleted, variants are gone for good. Double-check your SKUs or conditions before confirming a delete.',
    },
  },

  LIMITATIONS: {
    VARIANT_COUNT: {
      TITLE: 'How many variants can I create at once?',
      ITEMS: [
        { type: 'warning', bold: 'Up to 1,000 variants per range.', body: " If you try something like \"SKU-1 to SKU-5000\", it'll stop and tell you the range is too big. Just split it into smaller batches." },
        { type: 'warning', bold: 'Ranges go low to high, not backwards.', body: ' "SKU-100 to SKU-50" won\'t work — always start from the smaller value.' },
      ],
    },
    SKU_FORMATS: {
      TITLE: 'What SKU formats work for ranges?',
      TABLE_HEADERS: ['SKU style', 'Example', 'Works?'],
      TABLE_ROWS: [
        ['Plain numbers', '1000 to 1010', 'Yes'],
        ['Letters + dash + number', 'V-3000 to V-3010', 'Yes'],
        ['Letters + underscore + number', 'SKU_001 to SKU_010', 'Yes'],
        ['Number + one letter', '5A to 5Z', 'Yes'],
        ['Number + multiple letters', '5AA to 5ZZ', 'Not yet'],
        ['Mixed upper/lowercase prefix', 'V-3000 to v-3010', 'Not yet'],
      ],
    },
    FIELDS: {
      TITLE: 'Things to keep in mind about fields',
      ITEMS: [
        { type: 'warning', bold: 'Attributes must already exist on this product.', body: " You can't set an attribute that hasn't been added to the product yet — the AI will flag it if something's unrecognized." },
        { type: 'warning', bold: "You can't create a SKU that already exists.", body: " If a SKU is already taken, it'll be skipped and you'll see an error for it." },
        { type: 'info', bold: 'Weight is always saved in kg.', body: ' If you type "10 lbs", it\'ll automatically convert to kg. If you don\'t mention a unit, kg is assumed.' },
        { type: 'warning', bold: 'No "$" or currency symbols in prices.', body: ' Just use the number — write "49.99", not "$49.99" or "49.99 USD".' },
        { type: 'warning', bold: "Rental units must be from the system's list.", body: " If you use a unit that isn't configured (e.g. \"fortnights\"), it'll be rejected. Stick to what's available." },
        { type: 'info', bold: 'Country codes need to be uppercase.', body: ' Use "US" and "CA" when referring to country-specific warehouse prices.' },
      ],
    },
    CLONING: {
      TITLE: 'Copying from another variant (clone)',
      ITEMS: [
        { type: 'warning', bold: "The SKU you're copying from must actually exist.", body: ' If you say "clone SKU-999" but that variant doesn\'t exist, it\'ll fail.' },
        { type: 'info', bold: 'Only "clone" or "duplicate" triggers a full copy.', body: " Saying \"same material number as SKU X\" just copies that one field — it doesn't clone the whole variant." },
      ],
    },
    CONDITIONS: {
      TITLE: 'Filtering by conditions',
      ITEMS: [
        { type: 'info', bold: 'You can filter using:', body: ' equals, not equals, less than, greater than, between, contains, in a list, or not in a list. "Starts with" and "ends with" are partially supported but may not work in all cases.' },
        { type: 'info', bold: 'Avoid filter using:', body: ' pricing fields, unit of measurements and selectable/technical attributes.' },
        { type: 'warning', bold: '"Between" needs both ends.', body: ' If you say "weight between 2 and 5", both numbers are required — you can\'t leave one out.' },
      ],
    },
    INVALID_PROMPTS: {
      TITLE: "What kinds of prompts won't work?",
      ITEMS: [
        { type: 'error', bold: 'Only English is supported.', body: ' Prompts in other languages will be ignored.' },
        { type: 'error', bold: "Vague instructions don't work.", body: ' "Maybe add something" or "update a few" won\'t get you anywhere. Be specific — say exactly what you want and which variants to target.' },
        { type: 'error', bold: "Don't mix adding and deleting in one message.", body: ' Keep them in separate prompts to avoid unexpected results.' },
        { type: 'error', bold: 'You need to provide at least a quantity or SKU when creating.', body: ' A message like "create some variants" with no SKUs or count will be rejected.' },
        { type: 'info', bold: "Capitalisation doesn't matter for field names.", body: ' "Item Type", "item type", "ITEM TYPE" — they all mean the same thing.' },
      ],
    },
  },
};
